import { FormSelectDropdown } from "@/dentconnect/src/pages/job-request/components/form-select/FormSelectDropdown";
import { OrganizationSelect } from "@/dentconnect/src/pages/job-request/components/form-vendor-select/OrganizationSelect";
import { SYSTEM_STANDARD_FORM } from "@/dentconnect/src/pages/job-request/constants/system-standard-form";
import { NEW_VENDOR_ID } from "@/dentconnect/src/pages/job-request/types";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FormRenderer } from "@/dentconnect/src/pages/job-request/FormRenderer";
import { useJobRequestForm } from "@/dentconnect/src/pages/job-request/hooks/useJobRequestForm";
import { useConnectStore } from "@/dentlab/src/store/Connect";
import { useFormsStore } from "@/dentlab/src/store/Forms";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { Input } from "@/dentlab/src/components/ui/input";
import { SheetContentProps } from "../../sheet-provider/sheetRegistry";
import { useSheetStore } from "@/dentlab/src/store/Sheets/sheet.store";
import { invertRulesToFormData } from "@/dentlab/src/lib/utils/form-rule-field-mapper";
import { FormRule } from "@/dentlab/src/pages/form-builder/types/ruleTypes";

export interface JobRequestSheetContentProps {
	jobTitle: string;
	jobId: number | null;
	onSubmit?: () => void;
}

export const JobRequestSheetContent: React.FC<
	JobRequestSheetContentProps & SheetContentProps
> = ({ sheetKey, jobTitle, jobId, onSubmit }) => {
	const closeSheet = useSheetStore((state) => state.closeSheet);

	const { organization } = useCentralStore((state) => ({
		organization: state.organization,
	}));
	const { connectedVendors, brandsByOrgId, fetchConnectedVendors } =
		useConnectStore((state) => ({
			connectedVendors: state.connectedVendors,
			brandsByOrgId: state.brandsByOrgId,
			fetchConnectedVendors: state.fetchConnectedVendors,
		}));
	const formsByOrgId = useFormsStore((state) => state.formsByOrgId);
	const fetchForms = useFormsStore((state) => state.fetchForms);

	useEffect(() => {
		if (!organization || !organization.id) {
			return;
		}
		fetchConnectedVendors(organization.id);
	}, [organization]);

	const {
		selection,
		customFormData,
		loading,
		selectedForm,
		newVendorEmail,
		setNewVendorEmail,
		setCustomFormData,
		handleFormSelect,
		handleReset,
		handleSubmit,
	} = useJobRequestForm({
		vendors: connectedVendors,
		forms: formsByOrgId,
		fetchForms,
		myOrgId: organization?.id ?? undefined,
		myOrgName: organization?.name ?? undefined,
		onSuccess: () => {
			closeSheet(sheetKey);
		},
	});

	const applySubcontractPrefill = useCallback(
		(orgId: string, formId: string) => {
			if (!jobId || formId === SYSTEM_STANDARD_FORM.id) return;

			const form = formsByOrgId[orgId]?.[formId];
			const rules = form?.rules as FormRule[] | undefined;
			if (!rules || !Array.isArray(rules) || rules.length === 0) return;

			const job = useJobStore.getState().job;
			if (!job) return;

			const patientId = job.patient_id;
			const patient = patientId
				? usePatientStore.getState().patientsLookup[patientId] ?? null
				: null;

			const prefillData = invertRulesToFormData(
				rules,
				job,
				patient,
				form.definition.sections
			);

			if (Object.keys(prefillData).length > 0) {
				setCustomFormData(prefillData);
			}
		},
		[jobId, formsByOrgId, setCustomFormData]
	);

	// Apply prefill when initial form selection happens (auto-selected by useJobRequestForm hook)
	const prefillAppliedRef = useRef(false);
	useEffect(() => {
		if (
			prefillAppliedRef.current ||
			!jobId ||
			!selectedForm ||
			!selection.orgId ||
			Object.keys(customFormData).length > 0
		)
			return;

		prefillAppliedRef.current = true;
		applySubcontractPrefill(selection.orgId, selectedForm.id);
	}, [selectedForm?.id]);

	const handleFormSelectWithPrefill = useCallback(
		(orgId: string, formId: string) => {
			prefillAppliedRef.current = true;
			handleFormSelect(orgId, formId);
			applySubcontractPrefill(orgId, formId);
		},
		[handleFormSelect, applySubcontractPrefill]
	);

	const handleSubmitAndReload = async () => {
		await handleSubmit(jobId ?? undefined);
		onSubmit && onSubmit();
	};

	const vendorEmailMap = useMemo(() => {
		const map: Record<string, string> = {};
		for (const orgId of connectedVendors) {
			const brand = brandsByOrgId[orgId];
			if (brand?.admin_email) {
				map[brand.admin_email.toLowerCase()] = brand.name;
			}
		}
		return map;
	}, [connectedVendors, brandsByOrgId]);

	const isNewVendor = selection.orgId === NEW_VENDOR_ID;
	const isSystemFormFallback = selection.formId === SYSTEM_STANDARD_FORM.id;

	const matchedVendorName =
		isNewVendor && newVendorEmail.trim()
			? vendorEmailMap[newVendorEmail.trim().toLowerCase()]
			: undefined;

	return (
		<div>
			<div className="flex flex-col gap-2 p-4">
				<div className="flex items-center gap-2 text-lg font-bold">
					Fremdlabor beauftragen
				</div>
				<div className="text-sm text-gray-500">
					{jobId ? (
						<p>Wird verknüpft mit Auftrag: {jobTitle}</p>
					) : (
						<p className="text-red-500">
							Kein Auftrag ausgewählt. Der Auftrag wird nicht
							verknüpft.
						</p>
					)}
				</div>
			</div>
			<div className="flex flex-row gap-2 pb-6 px-4">
				<OrganizationSelect
					organizations={connectedVendors.map((orgId) => ({
						id: orgId,
						name: brandsByOrgId[orgId]?.name ?? "",
					}))}
					selectedOrganizationId={selection.orgId}
					setSelectedOrganizationId={(orgId) => {
						if (orgId === NEW_VENDOR_ID) {
							handleFormSelectWithPrefill(
								NEW_VENDOR_ID,
								SYSTEM_STANDARD_FORM.id
							);
						} else {
							const orgForms = formsByOrgId[orgId]
								? Object.values(formsByOrgId[orgId])
								: [];
							const firstPublished = orgForms.find(
								(f) => f.published_at
							);
							handleFormSelectWithPrefill(
								orgId,
								firstPublished?.id || SYSTEM_STANDARD_FORM.id
							);
						}
					}}
				/>

				{isNewVendor ? (
					<div className="flex flex-col gap-1">
						<Input
							type="email"
							placeholder="E-Mail des Fremdlabors"
							value={newVendorEmail}
							onChange={(e) => setNewVendorEmail(e.target.value)}
							className="w-[260px]"
							aria-label="E-Mail des neuen Fremdlabors"
							data-testid="new-vendor-email-input"
						/>
						{matchedVendorName && (
							<p className="text-xs text-amber-600">
								{matchedVendorName} ist bereits als Fremdlabor
								verbunden. Wählen Sie aus der Liste links aus.
							</p>
						)}
					</div>
				) : (
					selection.orgId &&
					!isSystemFormFallback && (
						<FormSelectDropdown
							orgId={selection.orgId}
							forms={
								formsByOrgId && formsByOrgId[selection.orgId]
									? Object.values(
											formsByOrgId[selection.orgId]
										)
									: []
							}
							selection={selection}
							onFormSelect={handleFormSelectWithPrefill}
						/>
					)
				)}
			</div>
			{selectedForm && (
				<FormRenderer
					customForm={selectedForm}
					customFormData={customFormData}
					onCustomFormDataChange={setCustomFormData}
					loading={loading}
					onSubmit={handleSubmitAndReload}
					onReset={handleReset}
				/>
			)}
		</div>
	);
};
